import { CLIENT_PRODUCTS } from "../constants/products";
import {
  AssetAllocationBucket,
  DistributorProfile,
  PortfolioBreakdown,
  PortfolioDistributionData,
  ProductBreakdownItem,
} from "../types";
import { findBreakdownByCategory } from "./funds";
import { titleCase } from "./string";

export const ALLOCATION_PCT_THRESHOLD = 0.1;

export const BUCKET_LABELS: Record<string, string> = {
  indian_equity: "Equity",
  debt: "Debt",
  commodity: "Commodity",
  global: "Global",
};

export const BREAKDOWN_SUB_CATEGORY: Record<string, string> = {
  indian_equity: "Market Cap",
  debt: "Credit Quality",
  commodity: "Commodity",
  global: "Market Exposure",
};

export function getProductBreakdownItemsFromBreakdown(
  breakdown: PortfolioBreakdown[],
  items: Array<{ key: string; label: string; subText?: string }>
): ProductBreakdownItem[] {
  return items.map((product) => {
    const pBreakdown = findBreakdownByCategory(breakdown, product.key);
    const investedValue = Number(pBreakdown?.values?.invested_value);
    const currentValue = Number(pBreakdown?.values?.current_value);

    return {
      key: product.key,
      label: product.label,
      subText: product.subText,
      currentValue: pBreakdown?.values?.current_value,
      allocationPct: pBreakdown?.values?.current_pct,
      // eslint-disable-next-line
      growthPct: !pBreakdown
        ? null
        : investedValue
          ? ((currentValue - investedValue) / investedValue) * 100
          : 0,
    };
  });
}

export function buildClientProductBreakdownItems(
  breakdown: PortfolioBreakdown | null,
  profile?: DistributorProfile | null
): ProductBreakdownItem[] {
  if (!profile) return [];

  const productWiseBreakdown = breakdown
    ? findBreakdownByCategory([breakdown], "product_wise_allocation")
    : null;
  const productBreakdown = productWiseBreakdown?.breakdown ?? [];

  const enabledProducts = CLIENT_PRODUCTS.filter((product) =>
    product.enabled(profile)
  );

  return getProductBreakdownItemsFromBreakdown(
    productBreakdown,
    enabledProducts
  );
}

// Same computation as buildClientProductBreakdownItems — kept as a distinct
// export so web's family-overview callers (which have no client/action to
// attach) can name their import for what it's building.
export const buildFamilyProductBreakdownItems =
  buildClientProductBreakdownItems;

export function getPortfolioDistributionFromBreakdown(
  breakdown: PortfolioBreakdown | null | undefined
): PortfolioDistributionData {
  if (!breakdown) {
    return { stocks: [], sectors: [] };
  }

  const stocksBreakdown = findBreakdownByCategory(
    [breakdown],
    "stock_wise_allocation"
  );
  const sectorBreakdown = findBreakdownByCategory(
    [breakdown],
    "sector_wise_allocation"
  );

  return {
    stocks: (stocksBreakdown?.breakdown ?? [])
      .map((stock) => ({
        label: stock.category,
        pct: stock.values?.current_pct ?? 0,
      }))
      .filter((row) => row.pct > ALLOCATION_PCT_THRESHOLD),
    sectors: (sectorBreakdown?.breakdown ?? [])
      .map((sector) => ({
        label: titleCase(sector.category.replace("_", " ")),
        pct: sector.values?.current_pct ?? 0,
      }))
      .filter((row) => row.pct > ALLOCATION_PCT_THRESHOLD),
  };
}

export function withScaledBarPct<T extends { pct: number }>(
  items: T[]
): (T & { barPct: number })[] {
  const total = items.reduce((sum, item) => sum + item.pct, 0);

  if (!total) {
    return items.map((item) => ({ ...item, barPct: item.pct }));
  }

  return items.map((item) => ({ ...item, barPct: (item.pct / total) * 100 }));
}

// Sorts items largest to smallest, scales the largest to 100% for the bar,
// and scales the rest relative to that max (rather than relative to the
// sum), so the biggest bar always fills the row and others compare against it.
export function withRelativeBarPct<T extends { pct: number }>(
  items: T[]
): (T & { barPct: number })[] {
  const sorted = [...items].sort((a, b) => b.pct - a.pct);
  const max = sorted[0]?.pct ?? 0;

  if (!max) {
    return sorted.map((item) => ({ ...item, barPct: 0 }));
  }

  return sorted.map((item) => ({ ...item, barPct: (item.pct / max) * 100 }));
}

function bucketLabel(category: string) {
  return BUCKET_LABELS[category] ?? titleCase(category.replace("_", " "));
}

export function getAssetAllocationBucketsFromBreakdown(
  breakdown: PortfolioBreakdown | null | undefined,
  bucketColors: Record<string, string>
): AssetAllocationBucket[] {
  if (!breakdown) return [];

  const assetAllocationBreakdown = findBreakdownByCategory(
    [breakdown],
    "asset_allocation"
  );

  return (assetAllocationBreakdown?.breakdown ?? [])
    .map((bucket) => ({
      key: bucket.category,
      label: bucketLabel(bucket.category),
      pct: bucket.values?.current_pct ?? 0,
      color: bucketColors[bucket.category],
      breakdownBy: BREAKDOWN_SUB_CATEGORY[bucket.category],
      breakdown: (bucket.breakdown ?? [])
        .map((sub) => ({
          label: sub.category.replace("_", " "),
          pct: sub.values?.current_pct ?? 0,
        }))
        .filter((sub) => sub.pct > ALLOCATION_PCT_THRESHOLD),
    }))
    .filter((bucket) => bucket.pct > ALLOCATION_PCT_THRESHOLD);
}
